/**
 * 🎛️ BANDZ BEATZ — Pocket Operator dans le navigateur.
 *
 * WHY THIS EXISTS (décision user 2026-11-04) : « le truc qui serait super
 * serait de pouvoir sampler des sons en direct ». La page Games (Tour Arcade)
 * accueille donc un 4e onglet : un sampler micro + séquenceur 16 pas, avec
 * export `.wav` et projets `.beatz.json`.
 *
 * RÈGLES DE CE FICHIER (elles tiennent tout le lot) :
 *  - ZÉRO OCTET SUR LE SERVEUR. Tout vit dans le navigateur : synthèse Web
 *    Audio, échantillons en mémoire, `localStorage` pour les slots, `Blob`
 *    pour les fichiers. Aucune requête réseau, donc ça marche en avion.
 *  - UN SEUL MOTEUR DE RENDU. Le direct et l'export `.wav` passent par la
 *    MÊME fonction pure `renderPatternPcm()` — ce qu'on entend est exactement
 *    ce qu'on exporte, et le rendu est déterministe (bruit à graine fixe).
 *  - L'audio n'est créé QU'AU PREMIER GESTE (iOS/Chrome refusent un
 *    AudioContext avant interaction). Aucun `AudioContext` au montage : en
 *    test (jsdom) la page se rend sans Web Audio du tout.
 *  - Décode/downsample/trim/normalise : un sample micro est ramené à 22050 Hz
 *    mono, silence de tête coupé (`< 0.02`) et crête normalisée à 0.95 pour
 *    que l'attaque tombe pile sur le temps.
 *  - i18n 100 % anglais (`games.beatz.*`) : aucun libellé en dur.
 *
 * RÉFÉRENCE DU LOT : `bandz-frontend/references/ui-roadcase-redesign-2026.md`
 * (section LOT BANDZ-BEATZ) et Obsidian `Changelog-2026-11-04.md`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* ====================================================================== */
/*  Modèle de projet                                                      */
/* ====================================================================== */

export const BEATZ_STEPS = 16;
export const BEATZ_TRACKS = 8;
/** Fréquence des échantillons stockés (mono). Compromis taille / tenue. */
export const SAMPLE_RATE = 22050;

export type TrackKind = 'kick' | 'snare' | 'clap' | 'closedHat' | 'openHat' | 'bass' | 'lead' | 'vox';

export interface TrackMeta {
  kind: TrackKind;
  /** Clé i18n courte affichée sur le pad (l'UI est EN par défaut). */
  labelKey: string;
  icon: string;
  /** Gain de base du synthé (le sampler garde le volume de piste). */
  gain: number;
}

/** Les 8 pistes sont un CONTRAT : index ↔ type ↔ libellé. */
export const TRACKS: readonly TrackMeta[] = [
  { kind: 'kick', labelKey: 'games.beatz.trackKick', icon: '🥁', gain: 0.95 },
  { kind: 'snare', labelKey: 'games.beatz.trackSnare', icon: '🪘', gain: 0.7 },
  { kind: 'clap', labelKey: 'games.beatz.trackClap', icon: '👏', gain: 0.62 },
  { kind: 'closedHat', labelKey: 'games.beatz.trackClosedHat', icon: '🎩', gain: 0.4 },
  { kind: 'openHat', labelKey: 'games.beatz.trackOpenHat', icon: '🎺', gain: 0.34 },
  { kind: 'bass', labelKey: 'games.beatz.trackBass', icon: '🎸', gain: 0.8 },
  { kind: 'lead', labelKey: 'games.beatz.trackLead', icon: '🎹', gain: 0.55 },
  { kind: 'vox', labelKey: 'games.beatz.trackVox', icon: '🎤', gain: 0.6 },
];

/** Échantillon micro : mono, à `SAMPLE_RATE`, déjà trimé + normalisé. */
export interface BeatSample {
  rate: number;
  data: Float32Array;
  /** Nom du fichier source (ou « Mic ») — purement informatif. */
  name: string;
}

export interface BeatTrack {
  /** 16 booléens : le pas est-il actif ? */
  steps: boolean[];
  /** Pitch PAR PAS, en demi-tons (-12..+12). */
  pitches: number[];
  /** Pitch global de la piste, en demi-tons (-12..+12). */
  pitch: number;
  /** Volume de piste 0..1. */
  volume: number;
  /** Sample custom (micro ou fichier) — `null` = synthé intégré. */
  sample: BeatSample | null;
}

export interface BeatProject {
  version: 1;
  name: string;
  bpm: number;
  /** 0..60 (%) : retard des pas impairs. */
  swing: number;
  tracks: BeatTrack[];
}

export interface BeatFx {
  lowPass: boolean;
  halfPitch: boolean;
  stutter: boolean;
}

function emptyTrack(): BeatTrack {
  return {
    steps: Array<boolean>(BEATZ_STEPS).fill(false),
    pitches: Array<number>(BEATZ_STEPS).fill(0),
    pitch: 0,
    volume: 0.85,
    sample: null,
  };
}

export function emptyProject(): BeatProject {
  return {
    version: 1,
    name: 'Bandz Beat',
    bpm: 120,
    swing: 0,
    tracks: Array.from({ length: BEATZ_TRACKS }, emptyTrack),
  };
}

/** Copie profonde d'un projet — l'état React ne partage jamais ses tableaux. */
function cloneProject(project: BeatProject): BeatProject {
  return {
    ...project,
    tracks: project.tracks.map((track) => ({
      ...track,
      steps: [...track.steps],
      pitches: [...track.pitches],
      sample: track.sample ? { ...track.sample, data: new Float32Array(track.sample.data) } : null,
    })),
  };
}

/* ====================================================================== */
/*  DSP — helpers purs, déterministes (aucun Math.random dans le rendu)   */
/* ====================================================================== */

/** PRNG à graine : deux rendus du même beat donnent le même fichier. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Filtre passe-haut à un pôle (retourne un nouveau tableau). */
function highPass(data: Float32Array, sampleRate: number, cutoff: number): Float32Array {
  const rc = 1 / (2 * Math.PI * cutoff);
  const dt = 1 / sampleRate;
  const alpha = rc / (rc + dt);
  const out = new Float32Array(data.length);
  let prevIn = 0;
  let prevOut = 0;
  for (let i = 0; i < data.length; i += 1) {
    const x = data[i];
    const y = alpha * (prevOut + x - prevIn);
    out[i] = y;
    prevIn = x;
    prevOut = y;
  }
  return out;
}

/** Filtre passe-bas à un pôle — sert au FX « low-pass » et au rendu. */
function lowPass(data: Float32Array, sampleRate: number, cutoff: number): Float32Array {
  const rc = 1 / (2 * Math.PI * cutoff);
  const dt = 1 / sampleRate;
  const alpha = dt / (rc + dt);
  const out = new Float32Array(data.length);
  let prev = 0;
  for (let i = 0; i < data.length; i += 1) {
    prev += alpha * (data[i] - prev);
    out[i] = prev;
  }
  return out;
}

/** Ratio de pitch : -12 demi-tons = une octave en dessous. */
export function semitoneRatio(semitones: number): number {
  return Math.pow(2, semitones / 12);
}

function fadeTail(out: Float32Array, sampleRate: number): void {
  const fade = Math.min(out.length, Math.floor(sampleRate * 0.005));
  for (let i = 0; i < fade; i += 1) {
    out[out.length - 1 - i] *= i / fade;
  }
}

/* ---- Voix de synthèse (mono, Float32Array) --------------------------- */

function kickVoice(sampleRate: number): Float32Array {
  const seconds = 0.45;
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  const rnd = mulberry32(7);
  let phase = 0;
  for (let i = 0; i < out.length; i += 1) {
    const t = i / sampleRate;
    const freq = 40 + 115 * Math.exp(-t * 32);
    phase += (2 * Math.PI * freq) / sampleRate;
    // Attaque instantanée (exp(-t*400)) puis corps qui s'éteint.
    const body = Math.sin(phase) * Math.exp(-t * 7) * (1 - Math.exp(-t * 500));
    const click = (rnd() * 2 - 1) * Math.exp(-t * 260) * 0.18;
    out[i] = body + click;
  }
  fadeTail(out, sampleRate);
  return out;
}

function snareVoice(sampleRate: number): Float32Array {
  const seconds = 0.24;
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  const rnd = mulberry32(11);
  const noise = new Float32Array(out.length);
  for (let i = 0; i < noise.length; i += 1) noise[i] = rnd() * 2 - 1;
  const bright = highPass(noise, sampleRate, 1500);
  for (let i = 0; i < out.length; i += 1) {
    const t = i / sampleRate;
    const tone = Math.sin(2 * Math.PI * 185 * t) * Math.exp(-t * 20) * 0.5;
    out[i] = tone + bright[i] * Math.exp(-t * 22) * 0.7;
  }
  fadeTail(out, sampleRate);
  return out;
}

function clapVoice(sampleRate: number): Float32Array {
  const seconds = 0.3;
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  const rnd = mulberry32(13);
  const noise = new Float32Array(out.length);
  for (let i = 0; i < noise.length; i += 1) noise[i] = rnd() * 2 - 1;
  const band = lowPass(highPass(noise, sampleRate, 800), sampleRate, 3200);
  const bursts = [0, 0.012, 0.024, 0.04];
  for (let i = 0; i < out.length; i += 1) {
    const t = i / sampleRate;
    let env = 0;
    for (const at of bursts) {
      if (t >= at) env = Math.max(env, Math.exp(-(t - at) * 90));
    }
    // La queue plus longue donne le « clap » qui traîne en salle.
    out[i] = band[i] * (env * 0.9 + Math.exp(-t * 16) * 0.25);
  }
  fadeTail(out, sampleRate);
  return out;
}

function hatVoice(sampleRate: number, seconds: number, decay: number): Float32Array {
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  const rnd = mulberry32(seconds > 0.15 ? 19 : 17);
  const noise = new Float32Array(out.length);
  for (let i = 0; i < noise.length; i += 1) noise[i] = rnd() * 2 - 1;
  const bright = highPass(noise, sampleRate, 7000);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = bright[i] * Math.exp(-(i / sampleRate) * decay);
  }
  fadeTail(out, sampleRate);
  return out;
}

function bass303Voice(sampleRate: number, semitones: number): Float32Array {
  const seconds = 0.34;
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  const freq = 55 * semitoneRatio(semitones);
  let phase = 0;
  // SVF de Chamberlin : résonance qui « miaule » quand la coupure descend.
  let low = 0;
  let band = 0;
  const q = 0.55;
  for (let i = 0; i < out.length; i += 1) {
    const t = i / sampleRate;
    const cutoff = Math.min(sampleRate * 0.45, 240 + 2600 * Math.exp(-t * 14));
    const f = 2 * Math.sin((Math.PI * cutoff) / sampleRate);
    phase += freq / sampleRate;
    const saw = 2 * (phase - Math.floor(phase + 0.5)) * 0.75;
    const high = saw - low - q * band;
    band += f * high;
    low += f * band;
    out[i] = Math.tanh(low * 1.6) * Math.exp(-t * 5.5);
  }
  fadeTail(out, sampleRate);
  return out;
}

function leadVoice(sampleRate: number, semitones: number): Float32Array {
  const seconds = 0.4;
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  const freq = 220 * semitoneRatio(semitones);
  let phase = 0;
  for (let i = 0; i < out.length; i += 1) {
    const t = i / sampleRate;
    const vibrato = 1 + 0.004 * Math.sin(2 * Math.PI * 5 * t);
    phase += (freq * vibrato) / sampleRate;
    // FM légère + saturation douce = carré « mélodique », pas criard.
    const mod = Math.sin(2 * Math.PI * freq * 2 * t) * Math.exp(-t * 9) * 1.2;
    const sq = Math.tanh(4 * Math.sin(2 * Math.PI * phase + mod));
    out[i] = sq * Math.exp(-t * 4.5) * 0.55;
  }
  fadeTail(out, sampleRate);
  return out;
}

function voxVoice(sampleRate: number): Float32Array {
  // Laser « vox/FX » : balayage descendant, parfait pour se faire remplacer
  // par sa propre voix au micro.
  const seconds = 0.32;
  const out = new Float32Array(Math.floor(sampleRate * seconds));
  let phase = 0;
  for (let i = 0; i < out.length; i += 1) {
    const t = i / sampleRate;
    const freq = 180 + 1700 * Math.exp(-t * 9);
    phase += (2 * Math.PI * freq) / sampleRate;
    out[i] = Math.sin(phase) * Math.exp(-t * 7) * 0.7;
  }
  fadeTail(out, sampleRate);
  return out;
}

const VOICE_CACHE = new Map<string, Float32Array>();

/** Voix de synthé pour une piste + un pitch, mise en cache (rendu rapide). */
function synthVoice(kind: TrackKind, semitones: number, sampleRate: number): Float32Array {
  const key = `${kind}:${semitones}:${sampleRate}`;
  const hit = VOICE_CACHE.get(key);
  if (hit) return hit;
  let voice: Float32Array;
  switch (kind) {
    case 'kick':
      voice = kickVoice(sampleRate);
      break;
    case 'snare':
      voice = snareVoice(sampleRate);
      break;
    case 'clap':
      voice = clapVoice(sampleRate);
      break;
    case 'closedHat':
      voice = hatVoice(sampleRate, 0.07, 95);
      break;
    case 'openHat':
      voice = hatVoice(sampleRate, 0.33, 12);
      break;
    case 'bass':
      voice = bass303Voice(sampleRate, semitones);
      break;
    case 'lead':
      voice = leadVoice(sampleRate, semitones);
      break;
    default:
      voice = voxVoice(sampleRate);
      break;
  }
  // Note : les percussions sont rendues à pitch fixe (l'appelant passe 0) —
  // repitcher un kick de 12 demi-tons ne produirait qu'un clic inaudible.
  VOICE_CACHE.set(key, voice);
  return voice;
}

/* ====================================================================== */
/*  Rendu PCM — LA source unique du direct et de l'export                 */
/* ====================================================================== */

export interface RenderOptions {
  sampleRate?: number;
  /** Nombre de mesures (1 mesure = 16 pas). */
  bars?: number;
  fx?: BeatFx;
}

export interface PcmRender {
  pcm: Float32Array;
  sampleRate: number;
}

/**
 * Rend le pattern complet en PCM mono.
 *
 * Direct ET export passent par ici : pas de divergence « ça sonne différent
 * dans le fichier ». Le bruit est à graine fixe, donc deux exports du même
 * beat sont octet-identiques.
 */
export function renderPatternPcm(project: BeatProject, options: RenderOptions = {}): PcmRender {
  const sampleRate = options.sampleRate ?? 44100;
  const bars = options.bars ?? 1;
  const fx = options.fx ?? { lowPass: false, halfPitch: false, stutter: false };
  const stepDur = 60 / project.bpm / 4;
  const tail = 0.7;
  const totalSteps = BEATZ_STEPS * bars;
  const length = Math.max(1, Math.ceil((totalSteps * stepDur + tail) * sampleRate));
  const out = new Float32Array(length);

  for (let step = 0; step < totalSteps; step += 1) {
    const local = step % BEATZ_STEPS;
    // Swing : les pas impairs (2e, 4e… doubles-croches) sont retardés.
    const swingDelay = local % 2 === 1 ? (project.swing / 100) * stepDur * 0.5 : 0;
    const when = step * stepDur + swingDelay;

    project.tracks.forEach((track, index) => {
      if (!track.steps[local]) return;
      const meta = TRACKS[index];
      const semis = track.pitch + track.pitches[local];
      const gain = meta.gain * track.volume;
      const isPitched = meta.kind === 'bass' || meta.kind === 'lead' || Boolean(track.sample);
      const pitchRatio = semitoneRatio(semis) * (fx.halfPitch && isPitched ? 0.5 : 1);

      if (track.sample) {
        mixSample(out, track.sample, when, pitchRatio, gain, sampleRate);
      } else {
        const voice = synthVoice(meta.kind, isPitched ? semis : 0, sampleRate);
        mixAt(out, voice, when, gain, sampleRate);
      }

      // Stutter : la frappe est retriggée 3× sur le quart du pas.
      if (fx.stutter) {
        for (let k = 1; k <= 3; k += 1) {
          const at = when + (k * stepDur) / 4;
          const decay = 1 - k * 0.25;
          if (track.sample) mixSample(out, track.sample, at, pitchRatio, gain * decay, sampleRate);
          else {
            const voice = synthVoice(meta.kind, isPitched ? semis : 0, sampleRate);
            mixAt(out, voice, at, gain * decay, sampleRate);
          }
        }
      }
    });
  }

  let pcm: Float32Array = out;
  if (fx.lowPass) pcm = lowPass(pcm, sampleRate, 850);

  // Écrêtage doux : jamais de distorsion numérique sur un export.
  let peak = 0;
  for (let i = 0; i < pcm.length; i += 1) peak = Math.max(peak, Math.abs(pcm[i]));
  if (peak > 0.98) {
    const scale = 0.98 / peak;
    for (let i = 0; i < pcm.length; i += 1) pcm[i] *= scale;
  }
  return { pcm, sampleRate };
}

function mixAt(out: Float32Array, voice: Float32Array, when: number, gain: number, sampleRate: number): void {
  const start = Math.floor(when * sampleRate);
  for (let i = 0; i < voice.length; i += 1) {
    const at = start + i;
    if (at >= out.length) break;
    if (at < 0) continue;
    out[at] += voice[i] * gain;
  }
}

function mixSample(
  out: Float32Array,
  sample: BeatSample,
  when: number,
  ratio: number,
  gain: number,
  sampleRate: number,
): void {
  const start = Math.floor(when * sampleRate);
  const data = sample.data;
  // Ratio = lecture plus rapide/lente : on lit la source à `ratio` pas.
  const duration = data.length / ratio;
  for (let i = 0; i < duration; i += 1) {
    const at = start + i;
    if (at >= out.length) break;
    if (at < 0) continue;
    const src = i * ratio;
    const i0 = Math.floor(src);
    const i1 = Math.min(data.length - 1, i0 + 1);
    const frac = src - i0;
    if (i0 >= data.length) break;
    out[at] += (data[i0] * (1 - frac) + data[i1] * frac) * gain;
  }
}

/* ====================================================================== */
/*  Encodage WAV — 16 bits PCM, RIFF/WAVE standard                        */
/* ====================================================================== */

/**
 * Encode du PCM flottant en `.wav` 16 bits (RIFF/WAVE).
 *
 * Testé : `"RIFF"` aux octets 0..3 et `"WAVE"` aux octets 8..11.
 * `pcm` peut être mono (Float32Array) ou multi-canal (Float32Array[]).
 */
export function encodeWavFromPcm(
  pcm: Float32Array | Float32Array[],
  sampleRate: number = 44100,
): ArrayBuffer {
  const channels = Array.isArray(pcm) ? pcm : [pcm];
  const channelCount = Math.max(1, channels.length);
  const frames = channels[0]?.length ?? 0;
  const bytesPerSample = 2;
  const blockAlign = channelCount * bytesPerSample;
  const dataSize = frames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true); // taille du fichier - 8
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // taille du bloc fmt
  view.setUint16(20, 1, true); // PCM entier
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); // octets/seconde
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 8 * bytesPerSample, true);
  ascii(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let frame = 0; frame < frames; frame += 1) {
    for (let channel = 0; channel < channelCount; channel += 1) {
      const source = channels[channel] ?? channels[0];
      const value = Math.max(-1, Math.min(1, source[frame] ?? 0));
      // 16 bits signé : -1 → -32768, +1 → 32767.
      view.setInt16(offset, value < 0 ? value * 0x8000 : value * 0x7fff, true);
      offset += bytesPerSample;
    }
  }
  return buffer;
}

/* ====================================================================== */
/*  Échantillons : downsample, trim du silence, normalisation             */
/* ====================================================================== */

/** Rééchantillonne (interpolation linéaire) puis normalise + trim. */
export function prepareSample(
  mono: Float32Array,
  inputRate: number,
  targetRate: number = SAMPLE_RATE,
): Float32Array {
  if (mono.length === 0) return new Float32Array(0);
  let data: Float32Array;
  if (Math.abs(inputRate - targetRate) < 1) {
    data = new Float32Array(mono);
  } else {
    const ratio = inputRate / targetRate;
    const length = Math.max(1, Math.floor(mono.length / ratio));
    data = new Float32Array(length);
    for (let i = 0; i < length; i += 1) {
      const src = i * ratio;
      const i0 = Math.floor(src);
      const i1 = Math.min(mono.length - 1, i0 + 1);
      const frac = src - i0;
      data[i] = mono[i0] * (1 - frac) + mono[i1] * frac;
    }
  }
  return trimAndNormalize(data);
}

/**
 * Coupe le silence de tête (`|x| < threshold`) puis met la crête à 0.95.
 * Sans le trim, un sample micro de 700 ms démarre 400 ms après le temps et
 * tout le beat sonne « en retard » — c'est le piège n°1 d'un sampler.
 */
export function trimAndNormalize(data: Float32Array, threshold = 0.02, peakTarget = 0.95): Float32Array {
  let start = 0;
  while (start < data.length && Math.abs(data[start]) < threshold) start += 1;
  // Silence total : on garde au moins un échantillon pour ne pas casser l'API.
  const from = start >= data.length ? 0 : start;
  const out = new Float32Array(Math.max(1, data.length - from));
  let peak = 0;
  for (let i = 0; i < out.length; i += 1) {
    const v = data[from + i] ?? 0;
    out[i] = v;
    peak = Math.max(peak, Math.abs(v));
  }
  if (peak > 0.0001) {
    // Gain plafonné : un sample quasi silencieux ne doit pas devenir un mur
    // de bruit blanc simplement parce qu'on normalise sa crête.
    const gain = Math.min(peakTarget / peak, 40);
    for (let i = 0; i < out.length; i += 1) out[i] *= gain;
  }
  return out;
}

/* ---- Sérialisation base64 PCM Int16 (projet ET slots) ---------------- */

function pcmToBase64(data: Float32Array): string {
  const bytes = new Uint8Array(data.length * 2);
  let offset = 0;
  for (let i = 0; i < data.length; i += 1) {
    const value = Math.max(-1, Math.min(1, data[i]));
    const int = value < 0 ? value * 0x8000 : value * 0x7fff;
    bytes[offset] = int & 0xff;
    bytes[offset + 1] = (int >> 8) & 0xff;
    offset += 2;
  }
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

function base64ToPcm(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const length = Math.floor(bytes.length / 2);
  const out = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    const lo = bytes[i * 2];
    const hi = bytes[i * 2 + 1];
    let int = (hi << 8) | lo;
    if (int >= 0x8000) int -= 0x10000;
    out[i] = int < 0 ? int / 0x8000 : int / 0x7fff;
  }
  return out;
}

interface SerializedSample {
  rate: number;
  name: string;
  data: string;
}

interface SerializedProject {
  format: 'bandz.beatz';
  version: 1;
  name: string;
  bpm: number;
  swing: number;
  tracks: Array<{
    steps: boolean[];
    pitches: number[];
    pitch: number;
    volume: number;
    sample: SerializedSample | null;
  }>;
}

/** Projet → JSON `.beatz.json` (samples inclus en PCM Int16 base64). */
export function serializeProject(project: BeatProject): string {
  const payload: SerializedProject = {
    format: 'bandz.beatz',
    version: 1,
    name: project.name,
    bpm: project.bpm,
    swing: project.swing,
    tracks: project.tracks.map((track) => ({
      steps: track.steps.map(Boolean),
      pitches: track.pitches.map((p) => Math.round(p)),
      pitch: Math.round(track.pitch),
      volume: track.volume,
      sample: track.sample
        ? { rate: track.sample.rate, name: track.sample.name, data: pcmToBase64(track.sample.data) }
        : null,
    })),
  };
  return JSON.stringify(payload, null, 2);
}

/**
 * `.beatz.json` → projet. Tolérant : un fichier abîmé doit ouvrir une erreur
 * lisible, pas écraser le beat en cours avec `undefined`.
 */
export function parseProject(text: string): BeatProject {
  const raw: unknown = JSON.parse(text);
  if (typeof raw !== 'object' || raw === null) throw new Error('bad project');
  const data = raw as Partial<SerializedProject> & { tracks?: unknown };
  if (data.format !== 'bandz.beatz') throw new Error('bad project');
  if (!Array.isArray(data.tracks)) throw new Error('bad project');

  const base = emptyProject();
  const project: BeatProject = {
    version: 1,
    name: typeof data.name === 'string' && data.name.trim() ? data.name.slice(0, 60) : base.name,
    bpm: clamp(Number(data.bpm) || 120, 70, 170),
    swing: clamp(Number(data.swing) || 0, 0, 60),
    tracks: base.tracks.map((fallback, index) => {
      const track = data.tracks?.[index] as SerializedProject['tracks'][number] | undefined;
      if (!track) return fallback;
      const steps = Array.from({ length: BEATZ_STEPS }, (_, i) => Boolean(track.steps?.[i]));
      const pitches = Array.from({ length: BEATZ_STEPS }, (_, i) =>
        clamp(Math.round(Number(track.pitches?.[i]) || 0), -12, 12),
      );
      let sample: BeatSample | null = null;
      if (track.sample && typeof track.sample.data === 'string') {
        try {
          const decoded = base64ToPcm(track.sample.data);
          if (decoded.length > 0) {
            sample = {
              rate: clamp(Math.round(Number(track.sample.rate) || SAMPLE_RATE), 8000, 48000),
              name: typeof track.sample.name === 'string' ? track.sample.name.slice(0, 80) : 'sample',
              data: decoded,
            };
          }
        } catch {
          sample = null;
        }
      }
      const volume = Number(track.volume);
      return {
        steps,
        pitches,
        pitch: clamp(Math.round(Number(track.pitch) || 0), -12, 12),
        // Volume 0 est une valeur LÉGITIME (piste coupée) : ne pas la
        // remplacer par le défaut comme le ferait un `|| 0.85`.
        volume: Number.isFinite(volume) ? clamp(volume, 0, 1) : 0.85,
        sample,
      };
    }),
  };
  return project;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/* ====================================================================== */
/*  Presets (demos)                                                       */
/* ====================================================================== */

type Pattern = Readonly<Record<number, ReadonlyArray<number>>>; // track → steps actifs

function patternToTracks(pattern: Pattern, pitches: Readonly<Record<number, Readonly<Record<number, number>>>> = {}): BeatTrack[] {
  return Array.from({ length: BEATZ_TRACKS }, (_, index) => {
    const track = emptyTrack();
    for (const step of pattern[index] ?? []) {
      if (step >= 0 && step < BEATZ_STEPS) track.steps[step] = true;
    }
    const perStep = pitches[index] ?? {};
    for (const [step, semis] of Object.entries(perStep)) {
      const at = Number(step);
      if (at >= 0 && at < BEATZ_STEPS) track.pitches[at] = semis;
    }
    return track;
  });
}

export const PRESETS: ReadonlyArray<{
  id: string;
  labelKey: string;
  bpm: number;
  swing: number;
  pattern: Pattern;
  pitches?: Readonly<Record<number, Readonly<Record<number, number>>>>;
}> = [
  {
    id: 'industrial',
    labelKey: 'games.beatz.presetIndustrial',
    bpm: 96,
    swing: 0,
    pattern: {
      0: [0, 3, 8, 10, 11],
      1: [4, 12, 15],
      2: [4, 12],
      3: [0, 2, 4, 6, 8, 10, 12, 14],
      4: [7],
      5: [0, 3, 6, 8, 11, 14],
      7: [15],
    },
    pitches: { 5: { 0: -12, 3: -12, 6: -5, 8: -12, 11: -7, 14: -10 } },
  },
  {
    id: 'synthwave',
    labelKey: 'games.beatz.presetSynthwave',
    bpm: 112,
    swing: 8,
    pattern: {
      0: [0, 8, 10],
      1: [4, 12],
      3: [0, 2, 4, 6, 8, 10, 12, 14],
      4: [14],
      5: [0, 3, 6, 8, 11, 14],
      6: [2, 7, 10, 13],
    },
    pitches: { 5: { 0: 0, 3: 0, 6: -5, 8: -3, 11: -5, 14: -7 }, 6: { 2: 12, 7: 7, 10: 10, 13: 3 } },
  },
  {
    id: 'techno',
    labelKey: 'games.beatz.presetTechno',
    bpm: 138,
    swing: 0,
    pattern: {
      0: [0, 4, 8, 12],
      3: [2, 6, 10, 14],
      4: [6, 14],
      2: [5, 13],
      5: [0, 2, 4, 6, 8, 10, 12, 14],
      7: [11],
    },
    pitches: { 5: { 0: -12, 2: -12, 4: -5, 6: -12, 8: -3, 10: -12, 12: -7, 14: -12 } },
  },
];

/* ====================================================================== */
/*  localStorage — auto-save + 4 slots                                    */
/* ====================================================================== */

const AUTOSAVE_KEY = 'bandz.beatz.autosave';
const SLOT_IDS = ['A', 'B', 'C', 'D'] as const;
type SlotId = (typeof SLOT_IDS)[number];
const slotKey = (id: SlotId) => `bandz.beatz.slot.${id}`;

function readStored(key: string): BeatProject | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? parseProject(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, project: BeatProject): boolean {
  try {
    localStorage.setItem(key, serializeProject(project));
    return true;
  } catch {
    // Quota / navigation privée : on le DIT à l'utilisateur, on ne perd pas
    // le beat en mémoire pour autant.
    return false;
  }
}

function hasStored(key: string): boolean {
  try {
    return Boolean(localStorage.getItem(key));
  } catch {
    return false;
  }
}

/* ====================================================================== */
/*  Téléchargement / partage (mobile d'abord)                             */
/* ====================================================================== */

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Laisse le temps au navigateur de démarrer le téléchargement.
  window.setTimeout(() => URL.revokeObjectURL(url), 8000);
}

/** Partage natif (iPhone/Android) : WhatsApp, Fichiers, Notes vocales… */
async function shareBlob(blob: Blob, filename: string, title: string): Promise<boolean> {
  try {
    const file = new File([blob], filename, { type: blob.type });
    if (typeof navigator.canShare !== 'function' || !navigator.canShare({ files: [file] })) return false;
    await navigator.share({ files: [file], title });
    return true;
  } catch {
    return false;
  }
}

/** Décode… seulement si le navigateur a bien un AudioContext utilisable. */
type AudioContextCtor = typeof AudioContext;
function audioCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext ?? w.webkitAudioContext ?? null;
}

/* ====================================================================== */
/*  Waveform                                                              */
/* ====================================================================== */

function Waveform({ data }: { data: Float32Array }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(245, 196, 105, 0.14)';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#f5c469';
    ctx.lineWidth = 1;
    ctx.beginPath();
    const per = Math.max(1, Math.floor(data.length / width));
    for (let x = 0; x < width; x += 1) {
      let peak = 0;
      const from = x * per;
      for (let i = 0; i < per; i += 1) peak = Math.max(peak, Math.abs(data[from + i] ?? 0));
      const y = height / 2 - peak * (height / 2);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.beginPath();
    for (let x = 0; x < width; x += 1) {
      let peak = 0;
      const from = x * per;
      for (let i = 0; i < per; i += 1) peak = Math.max(peak, Math.abs(data[from + i] ?? 0));
      const y = height / 2 + peak * (height / 2);
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }, [data]);
  return <canvas ref={canvasRef} width={140} height={28} className="beatz-wave" aria-hidden="true" />;
}

/* ====================================================================== */
/*  Composant                                                             */
/* ====================================================================== */

export default function BandzBeatz() {
  const { t } = useTranslation();

  const [project, setProject] = useState<BeatProject>(() => readStored(AUTOSAVE_KEY) ?? emptyProject());
  const [selected, setSelected] = useState(0);
  const [selectedStep, setSelectedStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(-1);
  const [recording, setRecording] = useState(false);
  const [fx, setFx] = useState<BeatFx>({ lowPass: false, halfPitch: false, stutter: false });
  const [status, setStatus] = useState('');
  const [wavUrl, setWavUrl] = useState('');
  const [bars, setBars] = useState<2 | 4>(2);
  const [slots, setSlots] = useState<Record<SlotId, boolean>>(() => ({
    A: hasStored(slotKey('A')),
    B: hasStored(slotKey('B')),
    C: hasStored(slotKey('C')),
    D: hasStored(slotKey('D')),
  }));

  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const startedAtRef = useRef(0);
  const stepDurRef = useRef(0.125);
  const rafRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recTimerRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const projectInputRef = useRef<HTMLInputElement | null>(null);

  const track = project.tracks[selected] ?? project.tracks[0];
  const meta = TRACKS[selected];

  /* ---- Audio : contexte + chaîne maîtresse (créés au 1er geste) ------ */

  const ensureAudio = useCallback((): AudioContext | null => {
    if (ctxRef.current) return ctxRef.current;
    const Ctor = audioCtor();
    if (!Ctor) return null;
    try {
      const ctx = new Ctor();
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 18000;
      filter.Q.value = 0.7;
      const gain = ctx.createGain();
      gain.gain.value = 0.9;
      filter.connect(gain);
      gain.connect(ctx.destination);
      ctxRef.current = ctx;
      filterRef.current = filter;
      gainRef.current = gain;
      return ctx;
    } catch {
      return null;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch {
        /* déjà arrêté */
      }
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    cancelAnimationFrame(rafRef.current);
    setPlaying(false);
    setPlayhead(-1);
  }, []);

  /* ---- Rendu → AudioBuffer ------------------------------------------ */

  const renderBuffer = useCallback(
    (scene: BeatProject, context: AudioContext): AudioBuffer | null => {
      try {
        const { pcm, sampleRate } = renderPatternPcm(scene, { sampleRate: context.sampleRate, bars: 1 });
        const buffer = context.createBuffer(1, pcm.length, sampleRate);
        buffer.copyToChannel(new Float32Array(pcm), 0);
        return buffer;
      } catch {
        return null;
      }
    },
    [],
  );

  const startPlayback = useCallback(() => {
    const ctx = ensureAudio();
    if (!ctx) {
      setStatus(t('games.beatz.audioUnavailable'));
      return;
    }
    void ctx.resume();
    stopPlayback();
    const buffer = renderBuffer(project, ctx);
    if (!buffer) {
      setStatus(t('games.beatz.audioUnavailable'));
      return;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    if (filterRef.current) source.connect(filterRef.current);
    else source.connect(ctx.destination);
    const stepDur = 60 / project.bpm / 4;
    stepDurRef.current = stepDur;
    startedAtRef.current = ctx.currentTime;
    source.start();
    sourceRef.current = source;
    setPlaying(true);

    const tick = () => {
      const elapsed = ctx.currentTime - startedAtRef.current;
      const step = Math.floor(elapsed / stepDur) % BEATZ_STEPS;
      setPlayhead(step);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [ensureAudio, project, renderBuffer, stopPlayback, t]);

  const togglePlay = useCallback(() => {
    if (playing) stopPlayback();
    else startPlayback();
  }, [playing, startPlayback, stopPlayback]);

  /* ---- FX ------------------------------------------------------------ */

  useEffect(() => {
    const filter = filterRef.current;
    if (filter) filter.frequency.value = fx.lowPass ? 850 : 18000;
    const source = sourceRef.current;
    if (source) source.playbackRate.value = fx.halfPitch ? 0.5 : 1;
    if (source && ctxRef.current) {
      if (fx.stutter) {
        const elapsed = ctxRef.current.currentTime - startedAtRef.current;
        const slice = stepDurRef.current / 4;
        const start = Math.floor(elapsed / slice) * slice;
        source.loopStart = start;
        source.loopEnd = start + slice;
      } else {
        source.loopStart = 0;
        source.loopEnd = source.buffer?.duration ?? 0;
      }
    }
  }, [fx]);

  /* Arrêt propre au démontage : pas de boucle fantôme en changeant d'onglet. */
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  /* ---- Auto-save (débouncé) ----------------------------------------- */

  useEffect(() => {
    const id = window.setTimeout(() => writeStored(AUTOSAVE_KEY, project), 600);
    return () => window.clearTimeout(id);
  }, [project]);

  /* ---- Mutations ----------------------------------------------------- */

  const updateTrack = useCallback(
    (index: number, mutate: (track: BeatTrack) => BeatTrack) => {
      setProject((prev) => {
        const next = cloneProject(prev);
        next.tracks[index] = mutate(next.tracks[index]);
        return next;
      });
    },
    [],
  );

  const audition = useCallback(
    (index: number, step = 0) => {
      const ctx = ensureAudio();
      if (!ctx) return;
      void ctx.resume();
      const scene: BeatProject = cloneProject(project);
      const only = scene.tracks[index];
      only.steps = only.steps.map(() => false);
      only.steps[step] = true;
      const { pcm, sampleRate } = renderPatternPcm({ ...scene, bpm: 120 }, { sampleRate: ctx.sampleRate, bars: 1 });
      // On ne joue que le premier pas : `renderPatternPcm` remplit le reste de
      // silence, ce qui coûte quelques ms mais garantit le même son qu'en piste.
      const slice = Math.ceil((60 / 120 / 4 + 0.8) * sampleRate);
      const buffer = ctx.createBuffer(1, slice, sampleRate);
      buffer.copyToChannel(new Float32Array(pcm.subarray(0, slice)), 0);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(filterRef.current ?? ctx.destination);
      source.start();
    },
    [ensureAudio, project],
  );

  const selectTrack = useCallback(
    (index: number) => {
      setSelected(index);
      setSelectedStep(0);
      audition(index, 0);
    },
    [audition],
  );

  const toggleStep = useCallback(
    (trackIndex: number, step: number) => {
      updateTrack(trackIndex, (prev) => {
        const steps = [...prev.steps];
        steps[step] = !steps[step];
        return { ...prev, steps };
      });
      setSelected(trackIndex);
      setSelectedStep(step);
      audition(trackIndex, step);
    },
    [audition, updateTrack],
  );

  const setStepPitch = useCallback(
    (value: number) => {
      updateTrack(selected, (prev) => {
        const pitches = [...prev.pitches];
        pitches[selectedStep] = clamp(Math.round(value), -12, 12);
        return { ...prev, pitches };
      });
    },
    [selected, selectedStep, updateTrack],
  );

  const setTrackPitch = useCallback(
    (value: number) => updateTrack(selected, (prev) => ({ ...prev, pitch: clamp(Math.round(value), -12, 12) })),
    [selected, updateTrack],
  );

  const setTrackVolume = useCallback(
    (value: number) => updateTrack(selected, (prev) => ({ ...prev, volume: clamp(value, 0, 1) })),
    [selected, updateTrack],
  );

  /* ---- Sampling micro ------------------------------------------------ */

  const installSample = useCallback(
    (data: Float32Array, name: string) => {
      updateTrack(selected, (prev) => ({ ...prev, sample: { rate: SAMPLE_RATE, data, name } }));
      setStatus(t('games.beatz.sampleReady'));
    },
    [selected, t, updateTrack],
  );

  const decodeToSample = useCallback(
    async (ctx: AudioContext, arrayBuffer: ArrayBuffer, name: string) => {
      try {
        const decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
        const mono = decoded.getChannelData(0);
        const prepared = prepareSample(mono, decoded.sampleRate, SAMPLE_RATE);
        if (prepared.length < 64) {
          setStatus(t('games.beatz.sampleEmpty'));
          return;
        }
        installSample(prepared, name);
      } catch {
        setStatus(t('games.beatz.sampleFailed'));
      }
    },
    [installSample, t],
  );

  const stopRecording = useCallback(() => {
    window.clearTimeout(recTimerRef.current);
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  const startRecording = useCallback(async () => {
    const ctx = ensureAudio();
    if (!ctx) {
      setStatus(t('games.beatz.audioUnavailable'));
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus(t('games.beatz.micUnavailable'));
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setStatus(t('games.beatz.micUnavailable'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const chunks: BlobPart[] = [];
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        window.clearTimeout(recTimerRef.current);
        setRecording(false);
        stream.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
        recorderRef.current = null;
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        void blob.arrayBuffer().then((buffer) => decodeToSample(ctx, buffer, 'Mic'));
      };
      recorder.start();
      setRecording(true);
      setStatus(t('games.beatz.recording'));
      // 2 s max, comme un PO-33 : on arrête tout seul.
      recTimerRef.current = window.setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
      }, 2000);
    } catch {
      setRecording(false);
      setStatus(t('games.beatz.micDenied'));
    }
  }, [decodeToSample, ensureAudio, t]);

  const onAudioFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      const ctx = ensureAudio();
      if (!ctx) {
        setStatus(t('games.beatz.audioUnavailable'));
        return;
      }
      const buffer = await file.arrayBuffer();
      await decodeToSample(ctx, buffer, file.name.slice(0, 80));
    },
    [decodeToSample, ensureAudio, t],
  );

  const resetToSynth = useCallback(() => {
    updateTrack(selected, (prev) => ({ ...prev, sample: null }));
    setStatus(t('games.beatz.synthRestored'));
  }, [selected, t, updateTrack]);

  /* ---- Presets / Random / Clear -------------------------------------- */

  const applyPreset = useCallback((preset: (typeof PRESETS)[number]) => {
    setProject((prev) => ({
      ...prev,
      bpm: preset.bpm,
      swing: preset.swing,
      tracks: patternToTracks(preset.pattern, preset.pitches),
    }));
    setSelected(0);
    setSelectedStep(0);
  }, []);

  const randomize = useCallback(() => {
    setProject((prev) => ({
      ...prev,
      tracks: prev.tracks.map((trackState, index) => {
        // Les percussions sont plus denses que les mélodies : un random qui
        // met 8 pas de basse partout est inécoutable.
        const density = index <= 4 ? 0.3 : 0.18;
        const steps = trackState.steps.map(() => Math.random() < density);
        const pitches = steps.map((on) => (on && index >= 5 ? Math.round((Math.random() * 2 - 1) * 7) : 0));
        return { ...trackState, steps, pitches };
      }),
    }));
  }, []);

  const clearPattern = useCallback(() => {
    setProject((prev) => ({
      ...prev,
      tracks: prev.tracks.map((trackState) => ({
        ...trackState,
        steps: Array<boolean>(BEATZ_STEPS).fill(false),
        pitches: Array<number>(BEATZ_STEPS).fill(0),
      })),
    }));
  }, []);

  /* ---- Export WAV ---------------------------------------------------- */

  const exportWav = useCallback(async () => {
    try {
      const { pcm, sampleRate } = renderPatternPcm(project, { sampleRate: 44100, bars, fx });
      const wav = encodeWavFromPcm(pcm, sampleRate);
      const blob = new Blob([wav], { type: 'audio/wav' });
      const filename = `bandz-beatz-${project.bpm}bpm.wav`;
      setWavUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      const shared = await shareBlob(blob, filename, `${project.name} — Bandz Beatz`);
      if (!shared) downloadBlob(blob, filename);
      setStatus(t('games.beatz.exported'));
    } catch {
      setStatus(t('games.beatz.exportFailed'));
    }
  }, [bars, fx, project, t]);

  /* ---- Projet : fichier + slots ------------------------------------- */

  const saveProjectFile = useCallback(async () => {
    try {
      const json = serializeProject(project);
      const blob = new Blob([json], { type: 'application/json' });
      const filename = `${project.name.replace(/[^\w-]+/g, '-').toLowerCase() || 'bandz-beat'}.beatz.json`;
      const shared = await shareBlob(blob, filename, `${project.name} — Bandz Beatz`);
      if (!shared) downloadBlob(blob, filename);
      setStatus(t('games.beatz.savedFile'));
    } catch {
      setStatus(t('games.beatz.saveFailed'));
    }
  }, [project, t]);

  const onProjectFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      try {
        const text = await file.text();
        const loaded = parseProject(text);
        stopPlayback();
        setProject(loaded);
        setSelected(0);
        setSelectedStep(0);
        setStatus(t('games.beatz.loadedFile'));
      } catch {
        setStatus(t('games.beatz.badFile'));
      }
    },
    [stopPlayback, t],
  );

  const saveSlot = useCallback(
    (id: SlotId) => {
      const ok = writeStored(slotKey(id), project);
      setSlots((prev) => ({ ...prev, [id]: ok ? true : prev[id] }));
      setStatus(ok ? t('games.beatz.slotSaved', { slot: id }) : t('games.beatz.quotaError'));
    },
    [project, t],
  );

  const loadSlot = useCallback(
    (id: SlotId) => {
      const stored = readStored(slotKey(id));
      if (!stored) {
        setStatus(t('games.beatz.slotEmpty'));
        return;
      }
      stopPlayback();
      setProject(stored);
      setSelected(0);
      setSelectedStep(0);
      setStatus(t('games.beatz.slotLoaded', { slot: id }));
    },
    [stopPlayback, t],
  );

  /* ---- Rendu --------------------------------------------------------- */

  const stepPitch = track.pitches[selectedStep] ?? 0;
  const activeSteps = useMemo(() => track.steps.filter(Boolean).length, [track.steps]);
  const currentSlot = slots;

  return (
    <section className="game-panel beatz-panel" data-game="beatz">
      {/* ---- Transport ------------------------------------------------ */}
      <div className="beatz-transport" data-beatz-transport="1">
        <button
          type="button"
          className="btn btn-primary game-btn beatz-play"
          data-beatz-play={playing ? 'stop' : 'play'}
          onClick={togglePlay}
        >
          {playing ? `⏹ ${t('games.beatz.stop')}` : `▶ ${t('games.beatz.play')}`}
        </button>

        <label className="beatz-field">
          <span>{t('games.beatz.bpm')}</span>
          <input
            type="range"
            min={70}
            max={170}
            step={1}
            value={project.bpm}
            data-beatz-bpm
            onChange={(event) =>
              setProject((prev) => ({ ...prev, bpm: clamp(Number(event.target.value), 70, 170) }))
            }
          />
          <b>{project.bpm}</b>
        </label>

        <label className="beatz-field">
          <span>{t('games.beatz.swing')}</span>
          <input
            type="range"
            min={0}
            max={60}
            step={1}
            value={project.swing}
            data-beatz-swing
            onChange={(event) =>
              setProject((prev) => ({ ...prev, swing: clamp(Number(event.target.value), 0, 60) }))
            }
          />
          <b>{project.swing}%</b>
        </label>

        <div className="beatz-fx" role="group" aria-label={t('games.beatz.fx')}>
          <button
            type="button"
            className={`beatz-fx-btn${fx.lowPass ? ' on' : ''}`}
            data-beatz-fx="lowpass"
            aria-pressed={fx.lowPass}
            onClick={() => setFx((prev) => ({ ...prev, lowPass: !prev.lowPass }))}
          >
            {t('games.beatz.fxFilter')}
          </button>
          <button
            type="button"
            className={`beatz-fx-btn${fx.halfPitch ? ' on' : ''}`}
            data-beatz-fx="half"
            aria-pressed={fx.halfPitch}
            onClick={() => setFx((prev) => ({ ...prev, halfPitch: !prev.halfPitch }))}
          >
            {t('games.beatz.fxHalfPitch')}
          </button>
          <button
            type="button"
            className={`beatz-fx-btn${fx.stutter ? ' on' : ''}`}
            data-beatz-fx="stutter"
            aria-pressed={fx.stutter}
            onClick={() => setFx((prev) => ({ ...prev, stutter: !prev.stutter }))}
          >
            {t('games.beatz.fxStutter')}
          </button>
        </div>
      </div>

      {/* ---- Pads ----------------------------------------------------- */}
      <div className="beatz-pads" role="group" aria-label={t('games.beatz.tracks')}>
        {TRACKS.map((trackMeta, index) => {
          const trackState = project.tracks[index];
          const padsState = index === selected;
          const hits = trackState.steps.filter(Boolean).length;
          return (
            <button
              key={trackMeta.kind}
              type="button"
              className={`beatz-pad${padsState ? ' on' : ''}${trackState.sample ? ' sampled' : ''}`}
              data-beatz-pad={index}
              data-beatz-sampled={trackState.sample ? '1' : '0'}
              aria-pressed={padsState}
              onClick={() => selectTrack(index)}
            >
              <span className="beatz-pad-ic" aria-hidden="true">
                {trackMeta.icon}
              </span>
              <span className="beatz-pad-name">{t(trackMeta.labelKey)}</span>
              <span className="beatz-pad-meta">
                {trackState.sample ? <span className="beatz-badge">MIC</span> : null}
                <span className="beatz-pad-hits">{hits}</span>
              </span>
              {trackState.sample ? <Waveform data={trackState.sample.data} /> : null}
            </button>
          );
        })}
      </div>

      {/* ---- Inspecteur de piste -------------------------------------- */}
      <div className="beatz-inspector" data-beatz-inspector={selected}>
        <div className="beatz-inspector-head">
          <b>
            <span aria-hidden="true">{meta.icon}</span> {t(meta.labelKey)}
          </b>
          <span className="beatz-muted">
            {activeSteps} / {BEATZ_STEPS}
          </span>
          {track.sample ? <span className="beatz-badge">MIC</span> : null}
        </div>

        <div className="beatz-sampler">
          <button
            type="button"
            className="btn game-btn"
            data-beatz-sample-mic
            onClick={() => (recording ? stopRecording() : void startRecording())}
          >
            {recording ? `⏹ ${t('games.beatz.stopRec')}` : `🎙️ ${t('games.beatz.sampleMic')}`}
          </button>
          <button
            type="button"
            className="btn game-btn"
            data-beatz-load-audio
            onClick={() => fileInputRef.current?.click()}
          >
            📂 {t('games.beatz.loadAudio')}
          </button>
          {track.sample ? (
            <button type="button" className="btn game-btn" data-beatz-reset-synth onClick={resetToSynth}>
              ↺ {t('games.beatz.resetSynth')}
            </button>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            className="beatz-file-input"
            data-beatz-audio-input
            onChange={(event) => {
              void onAudioFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>

        <div className="beatz-inspector-row">
          <label className="beatz-field">
            <span>{t('games.beatz.pitch')}</span>
            <input
              type="range"
              min={-12}
              max={12}
              step={1}
              value={track.pitch}
              data-beatz-track-pitch
              onChange={(event) => setTrackPitch(Number(event.target.value))}
            />
            <b>
              {track.pitch > 0 ? '+' : ''}
              {track.pitch}
            </b>
          </label>
          <label className="beatz-field">
            <span>{t('games.beatz.volume')}</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(track.volume * 100)}
              data-beatz-track-volume
              onChange={(event) => setTrackVolume(Number(event.target.value) / 100)}
            />
            <b>{Math.round(track.volume * 100)}%</b>
          </label>
        </div>
      </div>

      {/* ---- Vue matricielle 8 × 16 (tout le beat d'un œil) ------------ */}
      <div className="beatz-matrix" role="group" aria-label={t('games.beatz.matrix')}>
        {TRACKS.map((trackMeta, trackIndex) => (
          <div
            key={trackMeta.kind}
            className={`beatz-mrow${trackIndex === selected ? ' on' : ''}`}
            data-beatz-matrix-row={trackIndex}
          >
            <span className="beatz-mrow-label" title={t(trackMeta.labelKey)}>
              {trackMeta.icon}
            </span>
            {project.tracks[trackIndex].steps.map((on, step) => (
              <button
                key={step}
                type="button"
                className={`beatz-mcell${on ? ' on' : ''}${playhead === step ? ' now' : ''}`}
                data-beatz-matrix-step={`${trackIndex}-${step}`}
                aria-label={`${t(trackMeta.labelKey)} ${step + 1}`}
                aria-pressed={on}
                onClick={() => toggleStep(trackIndex, step)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* ---- Grille 4×4 de la piste sélectionnée ---------------------- */}
      <div className="beatz-seq" role="group" aria-label={t('games.beatz.steps')}>
        {Array.from({ length: BEATZ_STEPS }, (_, step) => {
          const on = track.steps[step];
          const pitch = track.pitches[step];
          return (
            <button
              key={step}
              type="button"
              className={`beatz-step${on ? ' on' : ''}${playhead === step ? ' now' : ''}${
                step === selectedStep ? ' sel' : ''
              }`}
              data-beatz-step={step}
              aria-pressed={on}
              onClick={() => toggleStep(selected, step)}
            >
              <span className="beatz-step-n">{step + 1}</span>
              {on ? <span className="beatz-step-p">{pitch > 0 ? `+${pitch}` : pitch}</span> : null}
            </button>
          );
        })}
      </div>

      <label className="beatz-field beatz-step-pitch">
        <span>
          {t('games.beatz.stepPitch')} · {selectedStep + 1}
        </span>
        <input
          type="range"
          min={-12}
          max={12}
          step={1}
          value={stepPitch}
          disabled={!track.steps[selectedStep]}
          data-beatz-step-pitch
          onChange={(event) => setStepPitch(Number(event.target.value))}
        />
        <b>
          {stepPitch > 0 ? '+' : ''}
          {stepPitch}
        </b>
      </label>

      {/* ---- Presets --------------------------------------------------- */}
      <div className="beatz-presets" role="group" aria-label={t('games.beatz.presets')}>
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="btn game-btn"
            data-beatz-preset={preset.id}
            onClick={() => applyPreset(preset)}
          >
            {t(preset.labelKey)}
          </button>
        ))}
        <button type="button" className="btn game-btn" data-beatz-random onClick={randomize}>
          🎲 {t('games.beatz.random')}
        </button>
        <button type="button" className="btn game-btn" data-beatz-clear onClick={clearPattern}>
          🗑 {t('games.beatz.clear')}
        </button>
      </div>

      {/* ---- Export WAV ------------------------------------------------ */}
      <div className="beatz-card">
        <div className="beatz-card-head">
          <b>{t('games.beatz.exportTitle')}</b>
          <div className="beatz-bars" role="group" aria-label={t('games.beatz.exportTitle')}>
            <button
              type="button"
              className={`beatz-fx-btn${bars === 2 ? ' on' : ''}`}
              data-beatz-bars="2"
              aria-pressed={bars === 2}
              onClick={() => setBars(2)}
            >
              {t('games.beatz.bars2')}
            </button>
            <button
              type="button"
              className={`beatz-fx-btn${bars === 4 ? ' on' : ''}`}
              data-beatz-bars="4"
              aria-pressed={bars === 4}
              onClick={() => setBars(4)}
            >
              {t('games.beatz.bars4')}
            </button>
          </div>
        </div>
        <div className="beatz-card-actions">
          <button type="button" className="btn btn-primary game-btn" data-beatz-export-wav onClick={() => void exportWav()}>
            🎧 {t('games.beatz.exportWav')}
          </button>
          {wavUrl ? (
            <button
              type="button"
              className="btn game-btn"
              data-beatz-share-wav
              onClick={() => {
                void fetch(wavUrl)
                  .then((response) => response.blob())
                  .then((blob) =>
                    shareBlob(blob, `bandz-beatz-${project.bpm}bpm.wav`, `${project.name} — Bandz Beatz`).then(
                      (ok) => {
                        if (!ok) setStatus(t('games.beatz.shareUnavailable'));
                      },
                    ),
                  );
              }}
            >
              📤 {t('games.beatz.share')}
            </button>
          ) : null}
        </div>
        {wavUrl ? (
          <div className="beatz-player">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio controls src={wavUrl} data-beatz-wav-player />
            <p className="beatz-hint">{t('games.beatz.playerHint')}</p>
          </div>
        ) : null}
      </div>

      {/* ---- Projet : fichier + slots --------------------------------- */}
      <div className="beatz-card">
        <div className="beatz-card-head">
          <b>{t('games.beatz.project')}</b>
        </div>
        <label className="beatz-field">
          <span>{t('games.beatz.beatName')}</span>
          <input
            type="text"
            value={project.name}
            data-beatz-name
            maxLength={60}
            onChange={(event) => setProject((prev) => ({ ...prev, name: event.target.value.slice(0, 60) }))}
          />
        </label>
        <div className="beatz-card-actions">
          <button type="button" className="btn game-btn" data-beatz-save-file onClick={() => void saveProjectFile()}>
            💾 {t('games.beatz.saveFile')}
          </button>
          <button
            type="button"
            className="btn game-btn"
            data-beatz-load-file
            onClick={() => projectInputRef.current?.click()}
          >
            📂 {t('games.beatz.loadFile')}
          </button>
          <input
            ref={projectInputRef}
            type="file"
            accept=".json,.beatz.json,application/json"
            className="beatz-file-input"
            data-beatz-project-input
            onChange={(event) => {
              void onProjectFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>

        <div className="beatz-slots" role="group" aria-label={t('games.beatz.slots')}>
          {SLOT_IDS.map((id) => (
            <div key={id} className={`beatz-slot${currentSlot[id] ? ' filled' : ''}`}>
              <span className="beatz-slot-id">{id}</span>
              <button
                type="button"
                className="beatz-fx-btn"
                data-beatz-slot-save={id}
                onClick={() => saveSlot(id)}
              >
                {t('games.beatz.saveSlot')}
              </button>
              <button
                type="button"
                className="beatz-fx-btn"
                data-beatz-slot-load={id}
                disabled={!currentSlot[id]}
                onClick={() => loadSlot(id)}
              >
                {t('games.beatz.loadSlot')}
              </button>
            </div>
          ))}
        </div>
        <p className="beatz-hint">{t('games.beatz.autoSaveHint')}</p>
      </div>

      <p className="beatz-status" data-beatz-status={status} aria-live="polite">
        {status}
      </p>
      <p className="game-hint">{t('games.beatz.hint')}</p>
    </section>
  );
}
